import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  FaCalendarAlt,
  FaClock,
  FaExternalLinkAlt,
  FaLock,
  FaPlay,
  FaRocket,
  FaVideo,
} from 'react-icons/fa';
import useUser from '../../hooks/useUser';
import fetchWithTimeout from '../../utils/fetchWithTimeout';
import './LiveAlgoLab.css';

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  skipped: 'Skipped this week',
  completed: 'Recording pending',
  recording_processing: 'Recording processing',
  replay_published: 'Replay ready',
  cancelled: 'Cancelled',
};

function formatEastern(value) {
  if (!value) return 'Not scheduled yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not scheduled yet';
  return date.toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: 'America/New_York',
  });
}

function statusLabel(session) {
  if (!session) return '';
  if (session.isJoinOpen && session.joinUrl) return 'Open now';
  return STATUS_LABELS[session.sessionStatus] || STATUS_LABELS[session.recordingStatus] || 'Scheduled';
}

function displaySessionTitle(session) {
  const title = typeof session?.title === 'string' ? session.title.trim() : '';
  return title.replace(/^Live Algo Lab\s*[-:]\s*/i, '') || 'Tuesday build session';
}

function LiveSessionCard({ session, hasAccess }) {
  if (!session) {
    return (
      <section className="lal-member-card lal-next-card">
        <p className="lal-kicker">Next session</p>
        <h1>Next Tuesday is not posted yet.</h1>
        <p className="lal-lede">
          The join button will appear here once the next session is on the schedule.
        </p>
      </section>
    );
  }

  const skipped = session.sessionStatus === 'skipped';
  const joinUrl = typeof session.joinUrl === 'string' ? session.joinUrl.trim() : '';
  const canJoin = hasAccess && session.isJoinOpen && joinUrl && !skipped;

  return (
    <section className="lal-member-card lal-next-card">
      <div className="lal-member-card__header">
        <div>
          <p className="lal-kicker">Next session</p>
          <h1>{displaySessionTitle(session)}</h1>
        </div>
        <span className={`lal-status-pill lal-status-pill--${session.sessionStatus}`}>
          {statusLabel(session)}
        </span>
      </div>

      <div className="lal-session-meta">
        <span><FaCalendarAlt /> {formatEastern(session.scheduledAt)}</span>
        {!canJoin && <span><FaClock /> Opens {formatEastern(session.joinOpensAt)}</span>}
      </div>

      {skipped ? (
        <div className="lal-session-alert">
          <strong>No Lab this week.</strong>
          <span>{session.skipReason || 'The next scheduled session will show here when it is available.'}</span>
        </div>
      ) : null}

      <div className="lal-cta-row">
        {canJoin ? (
          <a className="lal-primary-btn" href={joinUrl} target="_blank" rel="noreferrer">
            <FaExternalLinkAlt /> Join Live Lab
          </a>
        ) : hasAccess ? (
          <div className="lal-join-state">
            <FaClock />
            <span>Join opens 60 minutes before and stays open for 6 hours after start.</span>
          </div>
        ) : (
          <Link className="lal-primary-btn" to="/live-algo-lab">
            <FaRocket /> Get Pro access
          </Link>
        )}
      </div>
    </section>
  );
}

function ReplayCard({ session, hasAccess }) {
  const replayUrl = typeof session.recordingUrl === 'string' ? session.recordingUrl.trim() : '';
  const hasReplay = hasAccess && replayUrl;

  return (
    <article className="lal-replay-card">
      <div>
        <p className="lal-replay-date">{formatEastern(session.scheduledAt)}</p>
        <h3>{displaySessionTitle(session)}</h3>
        {session.recordingSummary ? (
          <p>{session.recordingSummary}</p>
        ) : (
          <p>
            {session.sessionStatus === 'replay_published'
              ? 'Replay is available.'
              : 'Replay has not been published yet.'}
          </p>
        )}
      </div>

      <div className="lal-replay-actions">
        <span className={`lal-status-pill lal-status-pill--${session.sessionStatus}`}>
          {session.recordingStatus === 'published' ? 'Replay ready' : statusLabel(session)}
        </span>
        {hasReplay ? (
          <a className="lal-secondary-btn" href={replayUrl} target="_blank" rel="noreferrer">
            <FaPlay /> Watch replay
          </a>
        ) : hasAccess ? (
          <span className="lal-muted-note">Not published yet</span>
        ) : (
          <span className="lal-muted-note"><FaLock /> Pro archive</span>
        )}
      </div>

      {hasReplay && Array.isArray(session.recordingChapters) && session.recordingChapters.length > 0 && (
        <div className="lal-chapter-list">
          {session.recordingChapters.slice(0, 4).map((chapter) => (
            <span key={`${session.id}-${chapter.startSeconds}`}>{chapter.title}</span>
          ))}
        </div>
      )}
    </article>
  );
}

const LiveAlgoLabWatchPage = () => {
  const { isAuthenticated, authLoading, loginWithRedirect, getApiToken } = useUser();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadSummary = async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = { 'Content-Type': 'application/json' };
        if (isAuthenticated) {
          const token = await getApiToken();
          if (token) headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetchWithTimeout(`${process.env.REACT_APP_API_URL}/courses/live-algo-lab/summary`, {
          headers,
        });
        if (!response.ok) {
          throw new Error(`Live Algo Lab failed to load (${response.status})`);
        }
        const data = await response.json();
        if (mounted) setSummary(data);
      } catch (err) {
        if (mounted) setError(err.message || 'Live Algo Lab failed to load');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (!authLoading) loadSummary();

    return () => {
      mounted = false;
    };
  }, [authLoading, getApiToken, isAuthenticated]);

  const hasAccess = Boolean(summary?.hasAccess);
  const replayArchive = useMemo(() => summary?.replayArchive || [], [summary]);

  return (
    <main className="lal-page lal-watch-page">
      <Helmet>
        <title>Live Algo Lab Member Page | BotSpot</title>
        <meta
          name="description"
          content="Join Live Algo Lab and watch real member replays if your BotSpot plan includes Pro access."
        />
      </Helmet>

      <section className="lal-member-shell">
        <div className="lal-watch-heading">
          <p className="lal-kicker">Included with BotSpot Pro</p>
          <h1>Live Algo Lab</h1>
          <p>
            Join Rob on Tuesdays, bring ideas or stuck points, and catch published replays here.
          </p>
        </div>

        {loading ? (
          <section className="lal-member-card lal-loading-card">
            <FaVideo />
            <h2>Loading the Lab schedule...</h2>
          </section>
        ) : error ? (
          <section className="lal-member-card lal-session-alert">
            <strong>Live Algo Lab could not load.</strong>
            <span>{error}</span>
          </section>
        ) : !summary?.configured ? (
          <section className="lal-member-card lal-session-alert">
            <strong>Live Algo Lab is not connected to a course schedule yet.</strong>
            <span>Add the Live Algo Lab course/session rows so this page can show the join button and archive.</span>
          </section>
        ) : (
          <>
            <LiveSessionCard session={summary.nextSession} hasAccess={hasAccess} />

            {!hasAccess && (
              <section className="lal-member-card lal-locked-member-card">
                <FaLock />
                <div>
                  <h2>Pro members can join live and watch the archive.</h2>
                  <p>Upgrade to Pro to access the Tuesday room and published replays from this page.</p>
                </div>
                {isAuthenticated ? (
                  <Link className="lal-primary-btn" to="/live-algo-lab">
                    <FaRocket /> Get Pro access
                  </Link>
                ) : (
                  <button className="lal-primary-btn" type="button" onClick={() => loginWithRedirect?.()}>
                    <FaRocket /> Log in or upgrade
                  </button>
                )}
              </section>
            )}

            <section className="lal-member-card lal-archive-section">
              <div className="lal-member-card__header">
                <div>
                  <p className="lal-kicker">Replay archive</p>
                  <h2>Replays</h2>
                </div>
                <span className="lal-status-pill">{replayArchive.length} sessions</span>
              </div>

              {replayArchive.length === 0 ? (
                <div className="lal-empty-archive">
                  <FaVideo />
                  <h3>No Live Algo Lab replays have been published here yet.</h3>
                  <p>Future sessions will appear here after recording processing.</p>
                </div>
              ) : (
                <div className="lal-replay-grid">
                  {replayArchive.map((session) => (
                    <ReplayCard key={session.id} session={session} hasAccess={hasAccess} />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </section>
    </main>
  );
};

export default LiveAlgoLabWatchPage;
